// src/components/features/VehicleRecalls/VehicleRecallFilters/VehicleRecallFilters.tsx
//
// Vehicle recall-specific filters wrapper around ExpenseFiltersToolbar.
// Adds recall status filter as extraFilters (in drawer) and extraChips
// (in the active filters row).
//
// Draft state pattern:
// - Maintains draft value for status internally.
// - Exposes an ExtraFiltersHandle via ref so ExpenseFiltersDrawer can
//   call onOpen (sync applied → draft), onApply (commit draft → applied),
//   onClear (reset draft), and hasDraftValues (for Clear button visibility).
// - onApply returns the values being applied so the toolbar can include
//   them in onFiltersChange synchronously without stale-state issues.
// - getValues returns the current applied extra filter values for
//   non-Apply emits (search change, chip removal).

import React, { useCallback, useMemo, useRef, useState } from "react";
import { Badge, CloseButton } from "@mantine/core";
import { useTranslation } from "react-i18next";

import { ExpenseFiltersToolbar } from "@/components/features";
import { ExtraFiltersHandle } from "@/components/features/ExpenseFiltersToolbar/ExpenseFiltersDrawer";
import {
  VehicleRecallStatusSelector,
} from "../VehicleRecallStatusSelector";
import { VEHICLE_RECALL_STATUS, VehicleRecallStatusValue } from "@/utils/api";

// ============================================
// Config Map for Chip Labels/Colors
// ============================================

const RECALL_STATUS_CONFIG: Record<number, { labelKey: string; color: string }> = {
  [VEHICLE_RECALL_STATUS.ACTIVE]: {
    labelKey: "vehicleRecalls:status.active",
    color: "red",
  },
  [VEHICLE_RECALL_STATUS.DISMISSED]: {
    labelKey: "vehicleRecalls:status.dismissed",
    color: "gray",
  },
  [VEHICLE_RECALL_STATUS.RESOLVED]: {
    labelKey: "vehicleRecalls:status.resolved",
    color: "green",
  },
};

// ============================================
// Helper: build extra values object from status
// ============================================

function buildExtraValues(status: VehicleRecallStatusValue | null): Record<string, unknown> {
  const values: Record<string, unknown> = {};
  if (status !== null) {
    values.statusFilter = [status];
  }
  return values;
}

// ============================================
// Types
// ============================================

export interface VehicleRecallFiltersProps {
  /** Handler for refresh button click */
  onRefresh?: () => void;

  /** Whether refresh button is disabled */
  refreshDisabled?: boolean;

  /** Whether data is loading */
  loading?: boolean;

  /** Enable keyboard shortcuts (default: true) */
  enableShortcuts?: boolean;

  /** Whether a car must be selected */
  carRequired?: boolean;

  /**
   * Callback emitting a combined filter object built from all enabled sources.
   * Fires on: Apply in drawer, chip removal, debounced search keyword change.
   * Receives null when all filters are cleared / empty.
   */
  onFiltersChange?: (filters: Record<string, unknown> | null) => void;

  /** Additional action buttons (rendered after Add/Refresh) */
  extraActions?: React.ReactNode;
}

// ============================================
// Component
// ============================================

const VehicleRecallFiltersComponent: React.FC<VehicleRecallFiltersProps> = ({
  onRefresh,
  refreshDisabled = false,
  loading = false,
  enableShortcuts = true,
  carRequired = false,
  onFiltersChange,
  extraActions,
}) => {
  const { t } = useTranslation(["vehicleRecalls", "common"]);

  // ============================================
  // Applied & Draft State
  // ============================================

  const [appliedStatus, setAppliedStatus] = useState<VehicleRecallStatusValue | null>(null);
  const [draftStatus, setDraftStatus] = useState<VehicleRecallStatusValue | null>(null);

  // ============================================
  // ExtraFiltersHandle — lifecycle contract with ExpenseFiltersDrawer
  // ============================================

  const extraFiltersRef = useRef<ExtraFiltersHandle>({
    onOpen: () => {
      setDraftStatus(appliedStatus);
    },
    onApply: () => {
      setAppliedStatus(draftStatus);
      return buildExtraValues(draftStatus);
    },
    onClear: () => {
      setDraftStatus(null);
    },
    hasDraftValues: () => {
      return draftStatus !== null;
    },
    getValues: () => {
      return buildExtraValues(appliedStatus);
    },
  });

  // Keep the ref callbacks up to date with latest state/props
  extraFiltersRef.current.onOpen = () => {
    setDraftStatus(appliedStatus);
  };
  extraFiltersRef.current.onApply = () => {
    setAppliedStatus(draftStatus);
    return buildExtraValues(draftStatus);
  };
  extraFiltersRef.current.onClear = () => {
    setDraftStatus(null);
  };
  extraFiltersRef.current.hasDraftValues = () => {
    return draftStatus !== null;
  };
  extraFiltersRef.current.getValues = () => {
    return buildExtraValues(appliedStatus);
  };

  // ============================================
  // Handlers — Chip removal (removes applied value directly)
  // ============================================

  const handleRemoveStatus = useCallback(() => {
    setAppliedStatus(null);
  }, []);

  // ============================================
  // Build Extra Chips (based on APPLIED value, not draft)
  // ============================================

  const extraChips = useMemo(() => {
    if (appliedStatus === null) return undefined;

    const config = RECALL_STATUS_CONFIG[appliedStatus];
    if (!config) return undefined;

    return (
      <Badge
        key="recall-status"
        variant="light"
        color={config.color}
        size="lg"
        radius="sm"
        pr={3}
        style={{ flexShrink: 0 }}
        rightSection={
          <CloseButton
            size="xs"
            variant="transparent"
            onClick={handleRemoveStatus}
            aria-label={t("common:buttons.clear")}
          />
        }
      >
        {t(config.labelKey)}
      </Badge>
    );
  }, [appliedStatus, handleRemoveStatus, t]);

  // ============================================
  // Build Extra Filters Content (for drawer — uses DRAFT value)
  // ============================================

  const extraFilters = useMemo(() => {
    return (
      <VehicleRecallStatusSelector
        value={draftStatus}
        onChange={setDraftStatus}
        label={t("vehicleRecalls:filters.status", { defaultValue: "Status" })}
      />
    );
  }, [draftStatus, t]);

  // ============================================
  // Render
  // ============================================

  return (
    <ExpenseFiltersToolbar
      entityType="cars"
      onRefresh={onRefresh}
      refreshDisabled={refreshDisabled}
      loading={loading}
      carFilterMode="single"
      showDateFilter={false}
      showTagFilter={false}
      showRefresh={false}
      enableShortcuts={enableShortcuts}
      carRequired={carRequired}
      extraFilters={extraFilters}
      extraChips={extraChips}
      extraFiltersRef={extraFiltersRef}
      extraActions={extraActions}
      onFiltersChange={onFiltersChange}
    />
  );
};

export const VehicleRecallFilters = React.memo(VehicleRecallFiltersComponent);